import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined"
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined"
import LoadingButton from "@mui/lab/LoadingButton"
import Autocomplete from "@mui/material/Autocomplete"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import TextField from "@mui/material/TextField"
import Typography from "@mui/material/Typography"
import { useEffect, useState } from "react"
import { useSelector } from "react-redux"

import { parseApiErrorMessage } from "../../../common/apiErrors"
import { useAuthorizedAxiosClient } from "../../../common/axios"
import Column from "../../../common/components/Column"
import { isConcierge } from "../../users/accountRole"
import { selectProfile } from "../../users/reducer"
import DialogHeader from "../components/DialogHeader"
import {
  TemplateGroupTabs,
  useTemplatePickerTab,
} from "../components/templateGroups"

const DIALOG_Z_INDEX = 1900
const MENU_Z_INDEX = 2000

function normalizeTemplateList(data) {
  if (Array.isArray(data)) return data
  if (Array.isArray(data?.results)) return data.results
  return []
}

function mergeTemplates(inboxTemplates, campaigns) {
  const byId = new Map()
  ;[...inboxTemplates, ...campaigns].forEach((item) => {
    if (item?.id != null) {
      byId.set(item.id, {
        ...item,
        label: item.name || `Template ${item.id}`,
      })
    }
  })
  return [...byId.values()]
}

export default function MissedYouSmsButton({ candidate, visible }) {
  const profile = useSelector(selectProfile)
  const isConciergeUser = isConcierge(profile)
  const isVisible = Boolean(visible && isConciergeUser)
  const [dialogOpen, setDialogOpen] = useState(false)
  const [sent, setSent] = useState(false)

  useEffect(() => {
    if (!isVisible) {
      setDialogOpen(false)
      setSent(false)
    }
  }, [isVisible])

  if (!isVisible) return null

  const hasPhone = Boolean(candidate?.phone)

  return (
    <Column gap={0.75}>
      <Button
        variant="outlined"
        color="primary"
        startIcon={<SmsOutlinedIcon />}
        disabled={sent || !hasPhone}
        onClick={() => setDialogOpen(true)}
        sx={{
          textTransform: "none",
          fontWeight: 700,
          alignSelf: "flex-start",
        }}
      >
        Send missed-you SMS
      </Button>
      {!hasPhone ? (
        <Typography variant="body2" color="text.secondary">
          This lead has no phone number.
        </Typography>
      ) : null}
      {sent ? (
        <Typography variant="body2" color="success.main">
          Missed-you text sent.
        </Typography>
      ) : null}
      <MissedYouSmsDialog
        open={dialogOpen}
        candidate={candidate}
        onClose={() => setDialogOpen(false)}
        onSent={() => {
          setSent(true)
          setDialogOpen(false)
        }}
      />
    </Column>
  )
}

function MissedYouSmsDialog({ open, candidate, onClose, onSent }) {
  const axios = useAuthorizedAxiosClient()
  const [templates, setTemplates] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [selected, setSelected] = useState(null)
  const [inputValue, setInputValue] = useState("")
  const [showPreview, setShowPreview] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [error, setError] = useState("")
  const { tab, setTab, options } = useTemplatePickerTab(templates)

  useEffect(() => {
    if (!open) {
      setSelected(null)
      setInputValue("")
      setShowPreview(false)
      setError("")
      return undefined
    }

    let cancelled = false
    setIsLoading(true)
    Promise.all([
      axios
        .get("/crm/api/concierge-sms/inbox/templates/")
        .then(({ data }) => normalizeTemplateList(data))
        .catch(() => []),
      axios
        .get("/workflows/api/sms/select-campaign/")
        .then(({ data }) => normalizeTemplateList(data))
        .catch(() => []),
    ])
      .then(([inboxTemplates, campaigns]) => {
        if (!cancelled) setTemplates(mergeTemplates(inboxTemplates, campaigns))
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [axios, open])

  async function handleSendClicked() {
    if (!selected?.id || !candidate?.phone) return
    setIsSending(true)
    setError("")
    try {
      await axios.post(
        `/workflows/api/sms/send-campaign/${selected.id}/lead/`,
        {
          name: candidate.candidate_name || candidate.name || "",
          phone: candidate.phone,
          email: candidate.email || "",
          company_name: candidate.company_name || "",
        }
      )
      onSent()
    } catch (err) {
      setError(
        parseApiErrorMessage(
          err?.response?.data,
          "Could not send the missed-you text."
        )
      )
    } finally {
      setIsSending(false)
    }
  }

  const previewText = selected?.rendered_text || selected?.text || ""

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      sx={{ zIndex: DIALOG_Z_INDEX }}
    >
      <DialogHeader title="Send missed-you SMS" onClose={onClose} />
      <DialogContent>
        <Column gap={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Choose an SMS template, preview it, then send it to{" "}
            {candidate?.candidate_name || candidate?.name || "this lead"}.
          </Typography>
          <TemplateGroupTabs tab={tab} onChange={setTab} />
          <Autocomplete
            value={selected}
            onChange={(_, value) => {
              setSelected(value)
              setShowPreview(false)
              setError("")
            }}
            inputValue={inputValue}
            onInputChange={(_, value) => setInputValue(value)}
            options={options}
            loading={isLoading}
            getOptionLabel={(option) => option.label || option.name || ""}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderInput={(params) => (
              <TextField
                {...params}
                label="SMS template"
                placeholder="Select a template"
              />
            )}
            componentsProps={{
              popper: { sx: { zIndex: MENU_Z_INDEX } },
            }}
            noOptionsText={
              isLoading ? "Loading templates…" : "No SMS templates found"
            }
          />
          {templates.length === 0 && !isLoading ? (
            <Typography variant="body2" color="text.secondary">
              Add an SMS template in Campaigns or SMS inbox settings to send a
              missed-you text from here.
            </Typography>
          ) : null}
          {showPreview ? (
            <Column gap={0.75}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Template preview
              </Typography>
              <Typography
                variant="body2"
                color="text.secondary"
                whiteSpace="pre-wrap"
                sx={{
                  p: 1.5,
                  borderRadius: 1.5,
                  bgcolor: "colors.gray50",
                  border: "1px solid",
                  borderColor: "colors.gray200",
                }}
              >
                {previewText || "This template has no text."}
              </Typography>
            </Column>
          ) : null}
          {error ? (
            <Typography variant="body2" color="error">
              {error}
            </Typography>
          ) : null}
        </Column>
      </DialogContent>
      <DialogActions sx={{ flexWrap: "wrap", gap: 1 }}>
        <Button variant="outlined" color="inherit" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="outlined"
          color="primary"
          startIcon={<VisibilityOutlinedIcon />}
          disabled={!selected}
          onClick={() => setShowPreview(true)}
        >
          Preview
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isSending}
          disabled={!selected || !candidate?.phone}
          onClick={handleSendClicked}
        >
          Send
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
